import React, { useState } from "react";
import { MdLocationOn, MdCall, MdEmail, MdClose, MdMenu } from "react-icons/md";
import { NavLink, Link } from "react-router-dom";
import logo from "../images/logo.webp";

const navItems = [
  { name: "Home", to: "/" },
  { name: "About", to: "/about" },
  { name: "Products", to: "/products" },
  { name: "Gallery", to: "/gallery" },
  { name: "Contact", to: "/contact" },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="w-full relative z-50">
      {/* Top Utility Bar */}
      <div className="hidden md:block bg-[#071626] border-b border-slate-800 text-slate-300 text-xs py-2.5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <p className="flex items-center gap-2 tracking-wide font-medium">
            <span className="w-2 h-2 rounded-full bg-[#EB4438] inline-block animate-pulse"></span>
            Welcome to The Dynamic Export Import LTD
          </p>
          <div className="flex items-center gap-6 text-slate-300">
            <div className="flex items-center gap-1.5">
              <MdLocationOn className="text-[#EB4438] text-base shrink-0" />
              <span>House #874/D, Master para, Uttarkhan, Dhaka-1230</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="sticky top-0 bg-[#0B2239]/95 backdrop-blur-md border-b border-slate-800 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                <img
                  src={logo}
                  alt="The Dynamic Exim Logo"
                  className="h-12 w-auto object-contain brightness-105"
                />
              </Link>
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex items-center space-x-2 bg-slate-900/60 p-1.5 rounded-full border border-slate-700/60">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `px-5 py-2 rounded-full text-sm font-semibold tracking-wide transition-all duration-200 ${
                      isActive
                        ? "text-white bg-[#EB4438] shadow-md shadow-red-500/20"
                        : "text-slate-300 hover:text-white hover:bg-white/10"
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              ))}
            </div>

            {/* Desktop Contact CTA Quick Actions */}
            <div className="hidden lg:flex items-center divide-x divide-slate-700/60 text-white text-xs">
              <a
                href="tel:+8801941422183"
                className="flex items-center gap-3 pr-5 group hover:opacity-90 transition-opacity"
              >
                <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center text-[#EB4438] group-hover:bg-[#EB4438] group-hover:text-white transition-colors">
                  <MdCall className="text-lg" />
                </div>
                <div className="text-left">
                  <p className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">
                    Call us now!
                  </p>
                  <p className="font-bold text-sm text-slate-100">+880-1941422183</p>
                </div>
              </a>

              <a
                href="mailto:moin@dynamic-exim.com"
                className="flex items-center gap-3 pl-5 group hover:opacity-90 transition-opacity"
              >
                <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center text-[#EB4438] group-hover:bg-[#EB4438] group-hover:text-white transition-colors">
                  <MdEmail className="text-lg" />
                </div>
                <div className="text-left">
                  <p className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">
                    Talk with us!
                  </p>
                  <p className="font-bold text-sm text-slate-100">moin@dynamic-exim.com</p>
                </div>
              </a>
            </div>

            {/* Mobile Hamburger Button */}
            <div className="flex lg:hidden items-center gap-3">
              <a
                href="tel:+8801941422183"
                aria-label="Call Us"
                className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center text-[#EB4438]"
              >
                <MdCall className="text-lg" />
              </a>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle Navigation Menu"
                className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center text-white focus:outline-none"
              >
                {mobileMenuOpen ? (
                  <MdClose className="text-2xl text-[#EB4438]" />
                ) : (
                  <MdMenu className="text-2xl text-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Drawer / Dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-[#071626] border-t border-slate-800 px-6 py-6 space-y-4 shadow-2xl animate-fade-in">
            <ul className="space-y-1.5">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold tracking-wide transition-all ${
                        isActive
                          ? "bg-[#EB4438] text-white shadow-md shadow-red-500/20"
                          : "text-slate-200 hover:bg-white/10 hover:text-white"
                      }`
                    }
                  >
                    <span>{item.name}</span>
                    <span className="text-xs opacity-70">&rarr;</span>
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* Mobile Contact Quick Card */}
            <div className="pt-4 border-t border-slate-800 space-y-3 text-xs text-slate-300">
              <a
                href="tel:+8801941422183"
                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10"
              >
                <MdCall className="text-lg text-[#EB4438]" />
                <div>
                  <p className="text-[10px] uppercase text-slate-400 font-semibold">Phone</p>
                  <p className="font-bold text-white text-sm">+880-1941422183</p>
                </div>
              </a>

              <a
                href="mailto:moin@dynamic-exim.com"
                className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10"
              >
                <MdEmail className="text-lg text-[#EB4438]" />
                <div>
                  <p className="text-[10px] uppercase text-slate-400 font-semibold">Email</p>
                  <p className="font-bold text-white text-xs">moin@dynamic-exim.com</p>
                </div>
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
